import React from 'react';
import { Coins, ExternalLink } from 'lucide-react';
import siteConfig from '../config/contentConfig';

const Tokenomics = () => {
  return (
    <section
      id="tokenomics"
      data-testid="tokenomics-section"
      className="section-container bg-[#0C0E12]"
    >
      <div className="text-center space-y-4 mb-16">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">
          <span className="text-[#32B44A]">{siteConfig.token.symbol}</span>{' '}
          Tokenomics
        </h2>
        <div className="w-24 h-1 bg-[#FFD600] mx-auto rounded-full" />
        <p className="text-slate-300 max-w-3xl mx-auto text-lg">
          Transparent and fair distribution designed to reward our community and ensure sustainable growth.
        </p>
      </div>

      <div className="max-w-5xl mx-auto">
        {/* Allocation Table */}
        <div className="glass rounded-3xl p-8 sm:p-12 border border-yellow-500/20 card-hover relative overflow-hidden">
          {/* Background Image */}
          <div 
            className="absolute inset-0 opacity-10 pointer-events-none"
            style={{
              backgroundImage: 'url(https://customer-assets-4nw71qhi.emergentagent.net/job_bd24f06d-c257-44d3-b821-05bccf021a15/artifacts/k8pnj6u3_image.png)',
              backgroundSize: 'contain',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }}
          />
          
          {/* Content */}
          <div className="relative z-10">
          <h3 className="text-2xl sm:text-3xl font-bold mb-8 text-center">
            Token Allocation Breakdown
          </h3>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-yellow-500/20">
                  <th className="text-left py-4 px-4 text-sm uppercase tracking-wider font-semibold text-yellow-400/90">
                    Allocation
                  </th>
                  <th className="text-right py-4 px-4 text-sm uppercase tracking-wider font-semibold text-yellow-400/90">
                    $NANA
                  </th>
                  <th className="text-right py-4 px-4 text-sm uppercase tracking-wider font-semibold text-yellow-400/90">
                    %
                  </th>
                </tr>
              </thead>
              <tbody>
                {siteConfig.tokenomics.allocation.map((item, index) => (
                  <tr
                    key={index}
                    className="border-b border-yellow-500/10 hover:bg-yellow-500/5 transition-colors"
                  >
                    <td className="py-4 px-4">
                      <div className="flex flex-col space-y-1">
                        <div className="flex items-center space-x-3">
                          <span className="text-2xl">{item.emoji}</span>
                          <span className="text-slate-200 font-medium">
                            {item.category}
                          </span>
                        </div>
                        {item.contractAddress && (
                          <div className="ml-11 text-xs font-mono text-slate-400 break-all">
                            {item.contractAddress}
                          </div>
                        )}
                        {item.link && (
                          <a
                            href={item.link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-11 text-xs text-[#FFD600] hover:text-[#FFE033] transition-colors flex items-center space-x-1 w-fit"
                          >
                            <span>{item.link.label}</span>
                            <ExternalLink size={12} />
                          </a>
                        )}
                        {item.links && (
                          <div className="ml-11 flex flex-wrap gap-2">
                            {item.links.map((link, linkIndex) => (
                              <a
                                key={linkIndex}
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-[#FFD600] hover:text-[#FFE033] transition-colors flex items-center space-x-1 w-fit"
                              >
                                <span>{link.label}</span>
                                <ExternalLink size={12} />
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4 text-right font-mono text-[#FFD600] font-semibold">
                      {item.amount}
                    </td>
                    <td className="py-4 px-4 text-right font-mono text-[#32B44A] font-semibold">
                      {item.percentage}
                    </td>
                  </tr>
                ))}
                {/* Total Row */}
                <tr className="border-t-2 border-yellow-500/30 font-bold bg-yellow-500/5">
                  <td className="py-4 px-4 text-white uppercase tracking-wider">
                    Total
                  </td>
                  <td className="py-4 px-4 text-right font-mono text-[#FFD600] text-lg">
                    1B
                  </td>
                  <td className="py-4 px-4 text-right font-mono text-[#32B44A] text-lg">
                    100%
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-4">
            {siteConfig.tokenomics.allocation.map((item, index) => (
              <div
                key={index}
                className="bg-[#141822] rounded-xl p-4 border border-yellow-500/10"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl">{item.emoji}</span>
                    <span className="text-slate-200 font-medium text-sm">
                      {item.category}
                    </span>
                  </div>
                </div>
                {item.contractAddress && (
                  <div className="text-xs font-mono text-slate-400 mb-2 break-all">
                    {item.contractAddress}
                  </div>
                )}
                {item.link && (
                  <a
                    href={item.link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-[#FFD600] hover:text-[#FFE033] transition-colors flex items-center space-x-1 mb-2 w-fit"
                  >
                    <span>{item.link.label}</span>
                    <ExternalLink size={12} />
                  </a>
                )}
                {item.links && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {item.links.map((link, linkIndex) => (
                      <a
                        key={linkIndex}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-[#FFD600] hover:text-[#FFE033] transition-colors flex items-center space-x-1 w-fit"
                      >
                        <span>{link.label}</span>
                        <ExternalLink size={12} />
                      </a>
                    ))}
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="font-mono text-[#FFD600] font-semibold">
                    {item.amount}
                  </span>
                  <span className="font-mono text-[#32B44A] font-semibold">
                    {item.percentage}
                  </span>
                </div>
              </div>
            ))}
            {/* Total Card */}
            <div className="bg-gradient-to-r from-[#FFD600]/10 to-[#32B44A]/10 rounded-xl p-4 border-2 border-yellow-500/30">
              <div className="flex justify-between items-center">
                <span className="text-white font-bold uppercase tracking-wider">
                  Total
                </span>
                <div className="flex space-x-4">
                  <span className="font-mono text-[#FFD600] font-bold text-lg">
                    1B
                  </span>
                  <span className="font-mono text-[#32B44A] font-bold text-lg">
                    100%
                  </span>
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Tokenomics;
